/**
 * Panel pre režim „Pridávanie kábla ťahom".
 *
 * Panel má dva stavy, lebo robí dve rôzne veci:
 *   – NASTAVENIE (počet vlákien, bufre, kazeta) sa spraví raz na začiatku,
 *   – RIADENIE REŤAZCA (Späť, Reset, Dokončiť) treba počas celej práce.
 *
 * Predtým boli obe naraz v jednom boxe `320 px × (100vh − 100px)` bez jediného
 * @media pravidla — na telefóne zakryl skoro celý displej a technik sa nemal
 * kam trafiť do stĺpika. Preto sa po stlačení „Začať ťahať" nastavenia zbalia
 * a dole zostane úzka lišta; ozubeným kolieskom sa dajú kedykoľvek vrátiť.
 */
import React from 'react';
import './AddCableByClickPanel.css';
import { FIBER_COLORS, getAvailableBufferSizes } from '../../utils/fiberColors';
import { FiberCountPicker } from '../Common/FiberCountPicker';

interface AddCableByClickPanelProps {
  isActive: boolean;
  /** true = zbalené do úzkej lišty, mapa je voľná */
  collapsed: boolean;
  fiberCount: number;
  cableType: string;
  cassetteType: 'none' | 'passthrough' | 'splitter_1_2' | 'splitter_1_4' | 'splitter_1_8';
  bufferSize: 12 | 24 | null; // null = bez bufrov
  bufferColors: string[];
  /** true = z celej trasy bude JEDEN kábel a medzistĺpy budú podporné */
  jedenKabel: boolean;
  onJedenKabelChange: (hodnota: boolean) => void;
  chainLength: number;
  onFiberCountChange: (count: number) => void;
  onCableTypeChange: (type: string) => void;
  onCassetteTypeChange: (type: 'none' | 'passthrough' | 'splitter_1_2' | 'splitter_1_4' | 'splitter_1_8') => void;
  onBufferSizeChange: (size: 12 | 24 | null) => void;
  onBufferColorChange: (bufferIndex: number, color: string) => void;
  onReset: () => void;
  onFinish: () => void;
  onClose: () => void;
  /** „Začať ťahať" — zbalí nastavenia a uvoľní mapu */
  onStart: () => void;
  /** ⚙ — znovu vytiahne nastavenia */
  onExpand: () => void;
  /** Zruší posledný vytvorený úsek */
  onUndo: () => void;
  canUndo: boolean;
}

export const AddCableByClickPanel: React.FC<AddCableByClickPanelProps> = ({
  isActive,
  collapsed,
  fiberCount,
  cableType,
  cassetteType,
  bufferSize,
  bufferColors,
  jedenKabel,
  onJedenKabelChange,
  chainLength,
  onFiberCountChange,
  onCableTypeChange,
  onCassetteTypeChange,
  onBufferSizeChange,
  onBufferColorChange,
  onReset,
  onFinish,
  onClose,
  onStart,
  onExpand,
  onUndo,
  canUndo,
}) => {
  if (!isActive) return null;

  const bufferSizeOptions = getAvailableBufferSizes(fiberCount);
  const bufferCount = bufferSize ? Math.ceil(fiberCount / bufferSize) : 0;

  // ── Zbalený stav: len lišta s tlačidlami, mapa je celá voľná ──
  if (collapsed) {
    return (
      <div className="cable-chain-bar">
        <button
          className="chain-btn"
          onClick={onUndo}
          disabled={!canUndo}
          title="Zrušiť posledný úsek"
        >
          <span className="chain-btn-icon">↩️</span>
          <span className="chain-btn-label">Späť</span>
        </button>
        <button
          className="chain-btn"
          onClick={onReset}
          disabled={chainLength === 0}
          title="Začať reťazec odznova (káble zostanú)"
        >
          <span className="chain-btn-icon">🔄</span>
          <span className="chain-btn-label">Reset</span>
        </button>
        <button className="chain-btn chain-btn-primary" onClick={onFinish} title="Dokončiť">
          <span className="chain-btn-icon">✅</span>
          <span className="chain-btn-label">Dokončiť</span>
        </button>
        <button className="chain-btn" onClick={onExpand} title="Nastavenia kábla">
          <span className="chain-btn-icon">⚙️</span>
          <span className="chain-btn-label">Nastavenia</span>
        </button>
      </div>
    );
  }

  return (
    <div className="add-cable-by-click-panel">
      <div className="panel-header">
        <span className="panel-icon">🔗</span>
        <h3>Pridávanie kábla ťahom</h3>
        <button className="panel-close" onClick={onClose} title="Zavrieť">✕</button>
      </div>

      <div className="panel-divider" />

      <div className="panel-section">
        <label htmlFor="fiber-count">Počet vlákien:</label>
        <FiberCountPicker
          id="fiber-count"
          value={fiberCount}
          onChange={onFiberCountChange}
        />
      </div>

      <div className="panel-section">
        <label htmlFor="cable-type">Typ kábla:</label>
        <select
          id="cable-type"
          value={cableType}
          onChange={(e) => onCableTypeChange(e.target.value)}
        >
          <option value="single-mode">Single-mode</option>
          <option value="multi-mode">Multi-mode</option>
        </select>
      </div>

      <div className="panel-divider" />

      <div className="panel-section">
        <label>Konfigurácia bufrov:</label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="buffer-size"
              value="none"
              checked={bufferSize === null}
              onChange={() => onBufferSizeChange(null)}
            />
            <span>Bez bufrov</span>
          </label>
          {bufferSizeOptions.map((option) => (
            <label key={option.size} className="radio-label">
              <input
                type="radio"
                name="buffer-size"
                value={option.size}
                checked={bufferSize === option.size}
                onChange={(e) => onBufferSizeChange(parseInt(e.target.value) as 12 | 24)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {bufferSize !== null && (
        <div className="panel-section">
          <label>Farby bufrov:</label>
          {Array.from({ length: bufferCount }, (_, i) => (
            <div key={i} className="buffer-color-row">
              <span className="buffer-label">Bufer {i + 1}:</span>
              <select
                value={bufferColors[i] || FIBER_COLORS[i % FIBER_COLORS.length].name}
                onChange={(e) => onBufferColorChange(i, e.target.value)}
                className="buffer-color-select"
              >
                {FIBER_COLORS.map((color) => (
                  <option key={color.name} value={color.name}>
                    {color.name}
                  </option>
                ))}
              </select>
              <div
                className="color-preview"
                style={{
                  backgroundColor: FIBER_COLORS.find(c => c.name === (bufferColors[i] || FIBER_COLORS[i % FIBER_COLORS.length].name))?.color || '#ccc'
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="panel-divider" />

      <div className="panel-section">
        <label>Kazety na stĺpikoch:</label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="cassette-type"
              value="none"
              checked={cassetteType === 'none'}
              onChange={(e) => onCassetteTypeChange(e.target.value as any)}
            />
            <span>Bez kazety</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="cassette-type"
              value="passthrough"
              checked={cassetteType === 'passthrough'}
              onChange={(e) => onCassetteTypeChange(e.target.value as any)}
            />
            <span>Passthrough (1:1)</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="cassette-type"
              value="splitter_1_2"
              checked={cassetteType === 'splitter_1_2'}
              onChange={(e) => onCassetteTypeChange(e.target.value as any)}
            />
            <span>Splitter 1:2</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="cassette-type"
              value="splitter_1_4"
              checked={cassetteType === 'splitter_1_4'}
              onChange={(e) => onCassetteTypeChange(e.target.value as any)}
            />
            <span>Splitter 1:4</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="cassette-type"
              value="splitter_1_8"
              checked={cassetteType === 'splitter_1_8'}
              onChange={(e) => onCassetteTypeChange(e.target.value as any)}
            />
            <span>Splitter 1:8</span>
          </label>
        </div>
      </div>

      <div className="panel-divider" />

      <div className="panel-section">
        <label>Trasa</label>
        <label className="radio-label">
          <input
            type="radio"
            name="jeden-kabel"
            checked={!jedenKabel}
            onChange={() => onJedenKabelChange(false)}
          />
          <span>Každý úsek samostatný kábel</span>
        </label>
        <label className="radio-label">
          <input
            type="radio"
            name="jeden-kabel"
            checked={jedenKabel}
            onChange={() => onJedenKabelChange(true)}
          />
          <span>Jeden súvislý kábel</span>
        </label>
        <small className="form-hint">
          Pri súvislom kábli sa po stlačení „Dokončiť" všetky úseky spoja do jedného
          a stĺpy medzi nimi sa stanú podpornými — kábel cez ne prechádza neprerušený.
          Kazety sa na nich nevytvárajú.
        </small>
      </div>

      <div className="panel-divider" />

      {chainLength > 0 && (
        <div className="panel-info">
          <span className="info-label">Stĺpikov v reťazci:</span>
          <span className="info-value">{chainLength}</span>
        </div>
      )}

      <div className="panel-actions">
        <button className="btn-primary btn-start" onClick={onStart}>
          {chainLength > 0 ? '🗺️ Späť na mapu' : '▶️ Začať ťahať'}
        </button>
      </div>

      <div className="panel-hint">
        💡 Nastav kábel a stlač „{chainLength > 0 ? 'Späť na mapu' : 'Začať ťahať'}".
        Panel sa zbalí do lišty dole a budeš klikať na stĺpiky. Nastavenia sa
        odtiaľ vrátia ozubeným kolieskom.
      </div>
    </div>
  );
};

